<template>
  <div class="m-3" style="height: 90vh">
    <div class="grid grid-cols-12 items-center content-center gap-2">
      <el-select
          class="input-label col-span-12 md:col-span-2"
          v-model="selectedValue"
          @change="applyToSelected"
      >
        <el-option
            key="download"
            :label="$t('views.backups.downloadBackup')"
            value="download"
        >
        </el-option>
        <el-option
            key="delete"
            :label="$t('views.backups.deleteBackup')"
            value="delete"
        >
        </el-option>
      </el-select>

      <el-input
          :placeholder="$t('views.common.listView.searchRows')"
          class="w-full col-span-12 md:col-span-10"
          :prefix-icon="Search"
          v-model="searchTerm"
      />

<!--      <div class="w-full col-span-12 md:col-span-2">-->
<!--        <horizontal-button-->
<!--            :title="$t('views.backups.createBackup')"-->
<!--            @click.prevent="createBackup()"-->
<!--        />-->
<!--      </div>-->
    </div>

    <ag-grid-vue
        class="ag-theme-alpine mt-4 md:mt-0"
        :columnDefs="columnDefs"
        :rowData="rowData"
        rowSelection="single"
        :enable-rtl="$ctx.getDir() === 'rtl'"
        style="height: 85vh"
        :pagination="true"
        :paginationAutoPageSize="true"
        :defaultColDef="defaultColDef"
        @cell-double-clicked="onCellDoubleClicked"
        :onGridReady="onGridReady"
    >
    </ag-grid-vue>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

// components
import { AgGridVue } from 'ag-grid-vue3'
import { ColDef, GridApi, GridOptions } from 'ag-grid-community'
import HorizontalButton from '@/components/HorizontalButton.vue'
import { useI18n } from 'vue-i18n'
import { Search } from '@element-plus/icons-vue'
import {useSettingsStore} from "@/stores/settingsStore";
import {formatPostgresDate} from "@/utils/date";
import {filesize} from "filesize";

export default defineComponent({
  name: 'backups',

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          size: 'Size'
        },
        ar: {
          size: 'الحجم'
        }
      }
    })

    return {
      t,
      Search
    }
  },

  data() {
    const columnDefs: ColDef[] = []
    const rowData: any[] = []
    const gridOptions: GridOptions = {}
    const defaultColDef: ColDef = {}

    return {
      columnDefs,
      rowData,
      gridOptions,
      defaultColDef,

      searchTerm: '',
      selectedValue: '',
      gridApi: undefined as GridApi | undefined
    }
  },

  components: {
    AgGridVue,
    HorizontalButton
  },

  beforeMount() {
    this.defaultColDef = {
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 150
    }

    this.columnDefs = [
      {
        headerName: this.t('views.backups.backupName'),
        field: 'name',
        checkboxSelection: true,
        headerCheckboxSelection: true
      },
      {
        headerName: this.t('size'),
        field: 'size',
        cellStyle: { textAlign: 'center' },
        valueGetter: (params) => {
          if (!params.data) return ''
          const backup = params.node?.data as any
          if (backup.size) {
            return filesize(backup.size)
          }
          return ''
        }
      },
      {
        headerName: this.t('models.common.date'),
        field: 'date',
        cellStyle: { textAlign: 'center' },
        valueGetter: (params) => {
          if (!params.data) return ''
          const backup = params.node?.data as any
          if (backup.date) {
            return formatPostgresDate(String(backup.date))
          }
          return ''
        }
      },
    ]
  },

  mounted() {},

  watch: {
    searchTerm() {
      this.filterItems()
    }
  },

  methods: {
    async updateRowData() {
      this.gridApi?.showLoadingOverlay()

      try {
        const data = await this.$http.get<any[]>("/storage/backups")
        this.rowData = data
      } catch (error) {
        this.$alertModal.showDanger({
          title: error.title,
          body: error.body
        })
      }

      this.gridApi?.hideOverlay()
    },

    async onGridReady(params: any) {
      this.gridApi = params.api
      this.gridApi?.sizeColumnsToFit()
      await this.updateRowData()
    },

    async delete(backup: any) {
      if (confirm(this.$t('views.common.listView.selectedRows.deleteConfirm'))) {
        try {
          await this.$http.delete(`/storage/backups/${backup?.name}`)
          await this.updateRowData()
          this.$alertModal.showSuccess({
            title: this.$t('views.common.listView.selectedRows.deleteSuccess')
          })
        } catch (error) {
          this.$alertModal.showDanger({
            title: error.title,
            body: error.body
          })
        }
      }
    },

    async applyToSelected() {
      if (this.selectedValue === 'delete' || this.selectedValue === 'download') {
        const selected = this.gridApi?.getSelectedRows()

        if (selected?.length) {
          if (this.selectedValue === 'delete') {
            // send request to archive
            await this.delete(selected[0])
          } else if (this.selectedValue == 'download') {
            await this.download(selected[0])
          }

          // deselect
          selected.length = 0
        }
      }

      this.selectedValue = ''
    },

    filterItems() {
      this.gridApi?.setQuickFilter(this.searchTerm)
    },

    onCellDoubleClicked(event: any) {
      this.download(event.data)
    },

    async download(backup: any) {
      try {
        const response = await fetch(useSettingsStore().baseUrl + '/storage/backups/' + backup?.name, {
          headers: {
            'Session-Id': localStorage.getItem('Session-Id'),
            Authorization: 'Bearer ' + localStorage.getItem('token')
          }
        });
        if (!response.ok) throw new Error('Network response was not ok');
        const blob = await response.blob();
        const urlObject = window.URL.createObjectURL(blob);

        // Create a link element
        const link = document.createElement('a');
        link.href = urlObject;
        link.download = backup?.name || 'downloaded-file';

        // Append link to the body
        document.body.appendChild(link);

        // Programmatically click the link to trigger download
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(urlObject);
      } catch (error) {
        console.error('Error downloading the file:', error);
      }
    },

    createBackup() {

    }
  }
})
</script>

<style></style>
